import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Select, { ValueType } from 'react-select';
import PhoneInput from 'react-phone-input-2';
import Button, {
  ButtonSize,
  ButtonType
} from '../../shared/components/core-ui/button/button.component';
import {
  InputText,
  InputTextErrorMode,
  InputTextType
} from '../../shared/components/core-ui/input-text/input-text.component';
import { birdToast } from '../../shared/components/core-ui/toast';
import { VALIDATION_REGEX } from '../../shared/components/validation.config';
import { getCreatableRoles } from '../../shared/config/staff-roles.config';
import { UserRole } from '../../shared/enums/user-role.enum';
import type { FormElementData } from '../../shared/interfaces/form-element-data.interface';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';
import { hasRole } from '../../shared/services/helpers/auth-helper/auth-helper.service';
import { createStaffUser } from './data.service';

interface RoleOption {
  value: UserRole;
  label: string;
}

/**
 * Turn marketingSpecialist into "Marketing Specialist" — the role values are the API's
 * vocabulary, not something to show a human as-is.
 */
function roleLabel(role: UserRole): string {
  return role
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

export interface UserManagementCreateProps {
  /** Where Cancel and a successful create return to. */
  returnPath: string;
}

/**
 * functional component UserManagementCreateContainer
 */
export const UserManagementCreateContainer: React.FC<UserManagementCreateProps> = ({
  returnPath
}) => {
  const history = useHistory();

  const [name, setName] = useState<FormElementData<string>>({ value: '', isValid: false });
  const [email, setEmail] = useState<FormElementData<string>>({ value: '', isValid: false });
  const [password, setPassword] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [phone, setPhone] = useState<string>('');
  const [role, setRole] = useState<UserRole | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Only a superAdmin may hand out the admin role. The server enforces this too — see
  // creatable-role.middleware in admin-connect; this keeps the dropdown from offering a
  // choice that would come back 403.
  const roleOptions: RoleOption[] = useMemo(() => {
    const callerIsSuperAdmin = hasRole([UserRole.SUPER_ADMIN]);
    return getCreatableRoles(callerIsSuperAdmin).map((r) => ({ value: r, label: roleLabel(r) }));
  }, []);

  const isFormValid: boolean =
    name.isValid && email.isValid && password.isValid && !!role && phone.length > 0;

  const handleSubmit = () => {
    if (!isFormValid || !role) {
      return;
    }
    setIsSubmitting(true);
    createStaffUser({
      name: name.value,
      email: email.value,
      phone: phone.startsWith('+') ? phone : `+${phone}`,
      password: password.value,
      role
    })
      .then((result) => {
        if (isErrorInfo(result)) {
          birdToast.error(
            'Could not create the user. Check the email is not already registered, and that your role is allowed to create users.',
            'Create staff user'
          );
          return;
        }
        birdToast.success(
          `${name.value} was created as ${roleLabel(role)}. The password was emailed to ${
            email.value
          }.`,
          'Staff user created'
        );
        history.push(returnPath);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-gilroy-bold mb-6">Create Staff User</h1>

      <div className="mb-4">
        <label className="block mb-2 font-gilroy-medium">Name</label>
        <InputText
          data={{ value: name.value, placeholder: 'Full name' }}
          config={{
            required: true,
            showErrorMode: InputTextErrorMode.onFirstBlur,
            disabled: isSubmitting
          }}
          eventHandlers={{
            onChangeHandler: (value, isValid) => setName({ value, isValid })
          }}
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2 font-gilroy-medium">Email</label>
        <InputText
          data={{ value: email.value, placeholder: 'name@birdnestlife.com' }}
          config={{
            required: true,
            type: InputTextType.email,
            validationRegex: VALIDATION_REGEX.EMAIL,
            showErrorMode: InputTextErrorMode.onFirstBlur,
            disabled: isSubmitting
          }}
          eventHandlers={{
            onChangeHandler: (value, isValid) => setEmail({ value, isValid })
          }}
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2 font-gilroy-medium">Phone</label>
        <PhoneInput
          country={'eg'}
          value={phone}
          disabled={isSubmitting}
          onChange={(value: string) => setPhone(value)}
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2 font-gilroy-medium">Password</label>
        <InputText
          data={{ value: password.value, placeholder: 'At least 6 characters' }}
          config={{
            required: true,
            type: InputTextType.password,
            validationRegex: VALIDATION_REGEX.PASSWORD,
            showErrorMode: InputTextErrorMode.onFirstBlur,
            disabled: isSubmitting
          }}
          eventHandlers={{
            onChangeHandler: (value, isValid) => setPassword({ value, isValid })
          }}
        />
        <p className="mt-1 text-sm text-slate-600">
          The new user is emailed this password by the authenticator, so treat it as a
          first-login credential and ask them to change it.
        </p>
      </div>

      <div className="mb-6">
        <label className="block mb-2 font-gilroy-medium">Role</label>
        <Select
          options={roleOptions}
          isDisabled={isSubmitting}
          placeholder="Select a role"
          onChange={(option: ValueType<RoleOption, false>) =>
            setRole(option ? option.value : undefined)
          }
        />
      </div>

      <div className="flex gap-3">
        <Button
          type={ButtonType.primary}
          size={ButtonSize.medium}
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          loading={isSubmitting}
        >
          Create user
        </Button>
        <Button
          type={ButtonType.gray}
          size={ButtonSize.medium}
          disabled={isSubmitting}
          onClick={() => history.push(returnPath)}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
